import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DeleteButton } from '../../components/ActionButtons';
import { apiRequest } from '../../services/api';
import UsageInfo from '../../components/UsageInfo';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';

const STOK_IKONLAR = {
  'Su': '💧', 'Çay': '🍵', 'Latte': '☕', 'Nescafe': '☕', 'Meyve': '🍎',
  'Çikolata': '🍫', 'Mini Bar': '🍾', 'Havlu': '🛁', 'Bornoz': '🥋',
  'Terlik': '👡', 'Şampuan': '🧴', 'Diş': '🪥', 'Tıraş': '🪒', 'Sabun': '🧼',
  'El Kremi': '🧴', 'Duş': '🚿', 'Kahvaltı': '🍳', 'SPA': '💆', 'Transfer': '🚌',
};
const getStokIkon = ad => {
  const key = Object.keys(STOK_IKONLAR).find(k => ad?.toLocaleLowerCase('tr-TR').includes(k.toLocaleLowerCase('tr-TR')));
  return key ? STOK_IKONLAR[key] : '📦';
};

function Toggle({ aktif, onChange }) {
  return (
    <button onClick={onChange}
      className={`relative w-14 h-7 rounded-full transition-all duration-300 flex-shrink-0 shadow-inner ${aktif ? 'bg-emerald-500 shadow-emerald-200' : 'bg-gray-200 shadow-gray-100'}`}>
      <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-md transition-all duration-300 transform-gpu ${aktif ? 'translate-x-7 rotate-180' : 'translate-x-0'}`} />
    </button>
  );
}

export default function BukletTanimlari() {
  const { personel } = useAuth();
  const [odaTipleri, setOdaTipleri] = useState([]);
  const [stoklar, setStoklar] = useState([]);
  const [bukletler, setBukletler] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [aktifTab, setAktifTab] = useState(null);
  const [seciliStokId, setSeciliStokId] = useState('');
  const [miktar, setMiktar] = useState(1);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [eklendi, setEklendi] = useState(false);
  const [silOnay, setSilOnay] = useState(null);
  const [arama, setArama] = useState('');
  const [formHata, setFormHata] = useState(null);

  const veriYukle = useCallback(async () => {
    setYukleniyor(true); setHata(null);
    try {
      const [odaData, stokData, bukletData] = await Promise.all([
        apiRequest('OdaTipi'), apiRequest('stok'), apiRequest('buklet')
      ]);
      const tipler = odaData || [];
      setOdaTipleri(tipler);
      setStoklar(stokData || []);
      setBukletler(bukletData || []);
      setAktifTab(prev => prev ?? (tipler.length > 0 ? tipler[0].odaTipiId : null));
      setSeciliStokId(prev => prev ?? ((stokData?.length > 0) ? stokData[0].stokId : ''));
    } catch { setHata('Veriler yüklenemedi.'); }
    setYukleniyor(false);
  }, []);

  useEffect(() => { veriYukle(); }, [veriYukle]);

  const aktifTip = odaTipleri.find(o => String(o.odaTipiId) === String(aktifTab));
  const kapalimi = aktifTip ? !aktifTip.bukletAktifMi : false;
  const filtrelenmis = bukletler.filter(b =>
    String(b.odaTipiId) === String(aktifTab) &&
    (!arama || b.stokAd?.toLocaleLowerCase('tr-TR').includes(arama.toLocaleLowerCase('tr-TR')))
  );
  const kapaliSayisi = odaTipleri.filter(o => !o.bukletAktifMi).length;

  // StokDto'da stok miktarı "kalan" alanında geliyor (Miktar değil!)
  const seciliStok = stoklar.find(s => String(s.stokId) === String(seciliStokId));
  const seciliStokKalan = seciliStok?.kalan ?? null;

  async function handleToggle() {
    if (!aktifTip) return;
    const yeniDurum = !aktifTip.bukletAktifMi;
    setOdaTipleri(p => p.map(o => o.odaTipiId === aktifTab ? { ...o, bukletAktifMi: yeniDurum } : o));
    try { await apiRequest('buklet/toggle', 'PATCH', { odaTipiId: aktifTab, bukletAktifMi: yeniDurum }); }
    catch { setOdaTipleri(p => p.map(o => o.odaTipiId === aktifTab ? { ...o, bukletAktifMi: !yeniDurum } : o)); }
  }

  async function ekle() {
    if (!seciliStokId) { setFormHata('Stok seçiniz.'); return; }
    if (bukletler.some(b => b.odaTipiId === aktifTab && b.stokId === parseInt(seciliStokId))) {
      setFormHata('Bu ürün zaten tanımlı.'); return;
    }
    setFormHata(null); setKaydediliyor(true);
    try {
      await apiRequest('buklet', 'POST', { odaTipiId: aktifTab, stokId: parseInt(seciliStokId), miktar, otelId: personel?.otelId });
      await veriYukle(); setMiktar(1); setEklendi(true); setTimeout(() => setEklendi(false), 2000);
    } catch { setFormHata('Hata oluştu.'); }
    setKaydediliyor(false);
  }

  async function handleSil(id) {
    try { await apiRequest(`buklet/${id}`, 'DELETE'); setBukletler(p => p.filter(b => b.bukletTanimiId !== id)); setSilOnay(null); }
    catch { alert('Hata oluştu.'); }
  }

  if (yukleniyor) return <LoadingSpinner fullPage text="Buklet Bilgileri Hazırlanıyor..." />;

  return (
    <div className="p-3 md:p-6 space-y-7 min-h-screen bg-gray-50/50 animate-fadeIn" style={{ transform: 'translateZ(0)' }}>

      <PageHeader
        icon="🧴"
        iconColor="from-amber-500 to-orange-700"
        iconShadow="shadow-amber-200"
        title="Buklet Tanımları"
        subtitle="Oda ikram ve sarf malzeme yönetimi"
      >
        {kapaliSayisi > 0 && (
          <div className="bg-rose-50 border-2 border-rose-100 px-6 py-3 rounded-2xl text-[10px] font-black text-rose-600 uppercase tracking-widest shadow-xl shadow-rose-50 animate-pulse text-center">
            ⚠️ {kapaliSayisi} ODA TİPİ KAPALI
          </div>
        )}
      </PageHeader>

      {/* Main Card */}
      <div className="bg-white rounded-[2.5rem] border border-gray-200 shadow-xl overflow-hidden transition-all duration-300">

        {/* Tab Bar */}
        <div className="flex border-b border-gray-100 overflow-x-auto no-scrollbar bg-gray-50/30 sticky top-0 z-10 backdrop-blur-sm">
          {odaTipleri.map(tip => {
            const aktif = aktifTab === tip.odaTipiId;
            const sayi = bukletler.filter(b => b.odaTipiId === tip.odaTipiId).length;
            return (
              <button key={tip.odaTipiId}
                onClick={() => { setAktifTab(tip.odaTipiId); setFormHata(null); setArama(''); }}
                className={`relative flex-shrink-0 px-8 py-5 text-[10px] font-black transition-all border-b-4 uppercase tracking-[0.15em] flex items-center gap-3 active:scale-95 ${aktif ? 'border-amber-500 text-amber-700 bg-white shadow-sm' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
                {tip.ad}
                <span className={`px-2 py-1 rounded-lg text-[10px] font-black shadow-inner ${aktif ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-400'}`}>{sayi}</span>
                {!tip.bukletAktifMi && <span className="text-[8px] bg-rose-100 text-rose-500 px-1.5 py-0.5 rounded shadow-sm font-black leading-none">OFF</span>}
              </button>
            );
          })}
        </div>

        {aktifTip && (
          <div className="p-4 md:p-8 space-y-8 animate-fadeIn leading-none">

            {/* Status Card */}
            <div className={`p-6 md:p-8 rounded-[2.5rem] border-2 transition-all duration-500 flex flex-col md:flex-row md:items-center justify-between gap-6 ${kapalimi ? 'bg-gray-50 border-gray-100 grayscale opacity-60' : 'bg-emerald-50/50 border-emerald-100 shadow-2xl shadow-emerald-50/50'}`}>
              <div className="flex items-center gap-6 text-left">
                <div className={`w-16 h-16 rounded-[1.5rem] flex items-center justify-center text-white text-2xl shadow-lg flex-shrink-0 transition-all duration-500 transform-gpu ${kapalimi ? 'bg-gray-400' : 'bg-gradient-to-br from-emerald-500 to-teal-600 shadow-emerald-100'}`}>
                  {aktifTip.ad[0]}
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-black text-gray-800 tracking-tight uppercase truncate">{aktifTip.ad} Buklet Otomasyonu</h2>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1.5 leading-relaxed opacity-80">
                    {kapalimi ? '❌ BU ODA TİPİ İÇİN OTOMATİK STOK DÜŞÜMÜ PASİF.' : '✅ REZERVASYON ANINDA OTOMATİK STOK DÜŞÜMÜ AKTİF.'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 bg-white/50 p-4 rounded-3xl border border-white self-start md:self-center shadow-sm">
                <span className={`text-[10px] font-black uppercase tracking-widest transition-colors duration-300 ${kapalimi ? 'text-gray-400' : 'text-emerald-600'}`}>
                  {kapalimi ? 'PASİF' : 'AKTİF'}
                </span>
                <Toggle aktif={!kapalimi} onChange={handleToggle} />
              </div>
            </div>

            {/* Form + Table */}
            <div className={`space-y-6 transition-all duration-500 ${kapalimi ? 'opacity-30 pointer-events-none filter blur-[1px]' : ''}`}>

              {/* Add Form */}
              <div className="bg-gray-50/50 rounded-[2rem] border-2 border-dashed border-gray-100 p-6 md:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-8 h-8 bg-amber-500 text-white rounded-lg flex items-center justify-center font-black shadow-lg shadow-amber-100 flex-shrink-0 animate-bounce-subtle">＋</span>
                  <h3 className="text-sm font-black text-gray-800 uppercase tracking-widest">Yeni Malzeme Tanımla</h3>
                </div>
                {formHata && (
                  <div className="bg-rose-50 border-2 border-rose-100 text-rose-600 text-[10px] font-black uppercase px-5 py-3 rounded-2xl mb-6 tracking-widest animate-shake leading-none">
                    ⚠️ {formHata}
                  </div>
                )}

                <div className="flex flex-col lg:flex-row gap-6 items-stretch lg:items-start">

                  {/* Stok Seçimi */}
                  <div className="flex-1 min-w-0 text-left">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 block ml-1">MALZEME SEÇİMİ</label>
                    <div className="relative">
                      <select value={seciliStokId}
                        onChange={e => { setSeciliStokId(e.target.value); setFormHata(null); setMiktar(1); }}
                        className="w-full border-2 border-gray-100 rounded-2xl px-5 py-3.5 text-sm font-black bg-white focus:border-amber-400 focus:outline-none transition-all shadow-sm appearance-none pr-12 h-[54px]">
                        <option value="">— Ürün Seçiniz —</option>
                        {stoklar.map(s => (
                          <option key={s.stokId} value={s.stokId}>
                            {s.ad} — Stok: {s.kalan ?? 0} adet
                          </option>
                        ))}
                      </select>
                      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs bg-gray-50 w-7 h-7 flex items-center justify-center rounded-lg border border-gray-100">▼</span>
                    </div>
                    {seciliStokId && seciliStok && (
                      <p className={`text-[10px] font-black mt-2 ml-1 uppercase tracking-widest ${seciliStokKalan === 0 ? 'text-rose-500' : 'text-gray-400'}`}>
                        {seciliStokKalan === 0
                          ? "⚠️ STOKTA YOK — Tanımlayabilirsiniz ama check-in'de stok yetersiz olacak"
                          : `📦 Depoda ${seciliStokKalan} adet mevcut`}
                      </p>
                    )}
                  </div>

                  {/* Miktar */}
                  <div className="lg:w-48 text-left">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 block ml-1">MİKTAR</label>
                    <div className="flex border-2 border-gray-100 rounded-2xl bg-white shadow-sm focus-within:border-amber-400 overflow-hidden h-[54px] transition-all">
                      <button onClick={() => setMiktar(m => Math.max(1, m - 1))} className="px-5 text-gray-400 hover:text-amber-500 font-black transition-colors bg-gray-50/50 active:bg-amber-50">−</button>
                      <input type="number" value={miktar}
                        onChange={e => setMiktar(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-full text-center text-sm font-black focus:outline-none bg-transparent" />
                      <button onClick={() => setMiktar(m => m + 1)} className="px-5 text-gray-400 hover:text-amber-500 font-black transition-colors bg-gray-50/50 active:bg-amber-50">+</button>
                    </div>
                    {seciliStokId && seciliStok && seciliStokKalan !== null && seciliStokKalan > 0 && miktar > seciliStokKalan && (
                      <p className="text-[9px] font-black text-amber-600 mt-2 ml-1 uppercase tracking-widest">
                        ⚠️ Stok ({seciliStokKalan}) aşıldı
                      </p>
                    )}
                  </div>

                  <button onClick={ekle} disabled={kaydediliyor}
                    className={`px-10 py-3.5 rounded-2xl text-[10px] font-black shadow-2xl transition-all duration-300 uppercase tracking-widest h-[54px] active:scale-95 ${eklendi ? 'bg-emerald-500 text-white shadow-emerald-200' : 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-amber-200 hover:-translate-y-1'}`}>
                    {kaydediliyor ? '⌛ Bekleyin...' : eklendi ? '✅ TANIMLANDI' : '📦 TANIMLA'}
                  </button>
                </div>
              </div>

              {/* List */}
              <div className="bg-white rounded-[2.5rem] border border-gray-200 shadow-sm overflow-hidden min-h-[400px]">
                <div className="px-6 md:px-8 py-5 border-b border-gray-100 bg-gray-50/30 flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="relative w-full md:w-80">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                    <input value={arama} onChange={e => setArama(e.target.value)}
                      placeholder="Liste içi ara..."
                      className="w-full pl-11 pr-4 py-3.5 text-sm border-2 border-gray-100 rounded-2xl focus:border-amber-400 focus:outline-none bg-white transition-all shadow-sm font-bold" />
                  </div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest whitespace-nowrap">
                    Sistemde toplam <b className="text-amber-600 text-xs">{filtrelenmis.length}</b> kalem tanımlı
                  </p>
                </div>

                {/* Mobile */}
                <div className="md:hidden divide-y divide-gray-50 px-2 leading-none">
                  {filtrelenmis.length === 0 ? (
                    <EmptyState icon="🧴" text="Sonuç bulunamadı" />
                  ) : filtrelenmis.map(item => {
                    const stok = stoklar.find(s => s.stokId === item.stokId);
                    const kalan = stok?.kalan ?? null;
                    return (
                      <div key={item.bukletTanimiId} className="p-6 space-y-5 hover:bg-amber-50/20 transition-all rounded-[2rem] group">
                        <div className="flex items-center gap-5">
                          <div className="w-14 h-14 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl flex items-center justify-center text-3xl shadow-inner border border-gray-100 relative overflow-hidden group-hover:scale-110 transition-transform duration-500 transform-gpu">
                            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                            <span className="relative z-10">{getStokIkon(item.stokAd)}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-black text-gray-800 text-base mb-1 tracking-tight uppercase truncate leading-none">{item.stokAd}</p>
                            <p className="text-[10px] font-black text-amber-500 uppercase tracking-widest">📦 {item.miktar} adet tanımlı</p>
                            {kalan !== null && (
                              <p className={`text-[9px] font-black uppercase tracking-widest mt-0.5 ${kalan === 0 ? 'text-rose-500' : kalan < item.miktar ? 'text-amber-600' : 'text-emerald-600'}`}>
                                {kalan === 0 ? '🚫 Stokta Yok' : kalan < item.miktar ? `⚠️ Depoda ${kalan} adet` : `✅ Depoda ${kalan} adet`}
                              </p>
                            )}
                          </div>
                        </div>
                        <button onClick={() => setSilOnay(item.bukletTanimiId)}
                          className="w-full py-4 bg-rose-50 text-rose-500 rounded-2xl text-[10px] font-black uppercase tracking-widest border border-rose-100 shadow-sm transition-all active:scale-95">
                          🗑️ TANIMI KALDIR
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto leading-none">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50 text-[11px] font-black text-gray-400 uppercase tracking-widest">
                        <th className="py-5 px-8 text-left">MALZEME / STOK ADI</th>
                        <th className="py-5 px-8 text-center w-40">MİKTAR</th>
                        <th className="py-5 px-8 text-center w-48">DEPODA STOK</th>
                        <th className="py-5 px-8 text-right w-32">İŞLEMLER</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filtrelenmis.length === 0 ? (
                        <EmptyState as="td" colSpan={4} icon="🧴" text="Henüz bir malzeme tanımlanmamış" />
                      ) : filtrelenmis.map(item => {
                        const stok = stoklar.find(s => s.stokId === item.stokId);
                        const kalan = stok?.kalan ?? null;
                        const durum = kalan === null ? null : kalan === 0 ? 'empty' : kalan < item.miktar ? 'low' : 'ok';
                        return (
                          <tr key={item.bukletTanimiId} className="group hover:bg-amber-50/30 transition-colors duration-200">
                            <td className="py-5 px-8">
                              <div className="flex items-center gap-5 min-w-0 leading-none">
                                <div className="w-12 h-12 bg-white rounded-xl shadow-lg shadow-amber-50 border border-gray-100 flex-shrink-0 flex items-center justify-center text-2xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 transform-gpu">
                                  {getStokIkon(item.stokAd)}
                                </div>
                                <span className="font-black text-gray-800 uppercase tracking-tight truncate text-lg">{item.stokAd}</span>
                              </div>
                            </td>
                            <td className="py-5 px-8 text-center text-sm leading-none">
                              <span className="font-black text-gray-900 bg-gray-100 px-4 py-2 rounded-xl shadow-inner border border-gray-100">{item.miktar}</span>
                            </td>
                            <td className="py-5 px-8 text-center text-sm leading-none">
                              {durum === null && <span className="text-gray-300 font-bold text-xs">—</span>}
                              {durum === 'empty' && <span className="bg-rose-100 text-rose-600 font-black text-[10px] px-3 py-1.5 rounded-xl uppercase tracking-widest border border-rose-200">🚫 Stokta Yok</span>}
                              {durum === 'low' && <span className="bg-amber-50 text-amber-700 font-black text-[10px] px-3 py-1.5 rounded-xl uppercase tracking-widest border border-amber-200">⚠️ {kalan} adet</span>}
                              {durum === 'ok' && <span className="bg-emerald-50 text-emerald-700 font-black text-[10px] px-3 py-1.5 rounded-xl uppercase tracking-widest border border-emerald-200">✅ {kalan} adet</span>}
                            </td>
                            <td className="py-5 px-8 text-right font-black">
                              <DeleteButton onClick={() => setSilOnay(item.bukletTanimiId)} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <UsageInfo
        color="amber"
        description="Buklet Tanımları modülü, her oda tipine özel ikram, kozmetik ve sarf malzemelerini belirlemenize ve stok kontrolünü otomatikleştirmenize olanak tanır."
        scenarios={[
          { title: "Otomatik Stok Düşümü", content: "Check-in anında buklet kalemleri stoktan otomatik olarak düşülerek envanter takibini hatasız hale getirir." },
          { title: "Stok Durumu Takibi", content: "Her malzemenin depodaki mevcut stoğu tabloda gösterilir. Yetersiz stok turuncu, sıfır stok kırmızı renkte işaretlenir." },
          { title: "Oda Tipine Özel Setler", content: "VIP odalar için 'Mini Bar', standart odalar için temel malzemeleri ayrı ayrı tanımlayabilirsiniz." },
        ]}
      />

      {silOnay && createPortal(
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center px-4 backdrop-blur-md">
          <div className="bg-white rounded-[2.5rem] shadow-2xl p-10 w-full max-w-sm text-center border border-gray-100">
            <div className="w-24 h-24 bg-rose-50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 text-5xl shadow-xl shadow-rose-100/50 animate-bounce-subtle">🗑️</div>
            <h3 className="font-black text-gray-900 text-xl tracking-tight uppercase leading-none">Tanımı Kaldır</h3>
            <p className="text-[11px] font-black text-gray-500 mt-5 mb-10 leading-relaxed uppercase tracking-tight text-center">
              <b className="text-gray-900 font-black">"{bukletler.find(x => x.bukletTanimiId === silOnay)?.stokAd}"</b> artık bu oda tipi için hesaplanmayacak. Emin misiniz?
            </p>
            <div className="flex gap-4">
              <button onClick={() => setSilOnay(null)} className="flex-1 py-4 bg-gray-50 text-gray-600 rounded-2xl text-[10px] font-black hover:bg-gray-100 transition-all uppercase tracking-widest active:scale-95">İPTAL</button>
              <button onClick={() => handleSil(silOnay)} className="flex-1 py-4 bg-gradient-to-r from-rose-500 to-red-600 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-rose-200 transition-all uppercase tracking-widest active:scale-95">EVET, SİL</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
        @keyframes bounceSubtle { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .animate-bounce-subtle { animation: bounceSubtle 2s infinite; }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-5px); } 75% { transform: translateX(5px); } }
        .animate-shake { animation: shake 0.3s ease-in-out infinite; animation-iteration-count: 2; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}